import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { useI18n } from "@/i18n";
import { PageHeader, EmptyState, useSortable } from "@/components/common";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Warehouse } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import ExpiryTab from "@/components/inventory/ExpiryTab";
import TransfersTab from "@/components/inventory/TransfersTab";
import WriteOffsTab from "@/components/inventory/WriteOffsTab";
import WriteOffDialog from "@/components/inventory/WriteOffDialog";
import LotBbdDialog from "@/components/inventory/LotBbdDialog";
import TransferDialog from "@/components/inventory/TransferDialog";
import InventoryTable from "@/components/inventory/InventoryTable";
import OwnWithdrawals from "@/pages/OwnWithdrawals";
import PromotionsPage from "@/pages/Promotions";
import { AdjustDialog, HistoryDialog, RenameDialog, OwnWithdrawalDialog, PromotionDialog }
  from "@/components/inventory/InventoryDialogs";

const EMPTY_ADJ = { quantity_signed: "", quality: "good", reason: "", reclass: false };

const INV_ACC = {
  product: (r) => r.product_name || r.sku || "", physical: (r) => r.physical || 0,
  reserved: (r) => r.reserved || 0, available: (r) => r.available || 0,
  defect: (r) => r.defect || 0, second_choice: (r) => r.second_choice || 0,
  stock_value: (r) => r.stock_value || 0,
};

const hasStock = (r) => (r.physical || 0) !== 0 || (r.reserved || 0) !== 0
  || (r.defect || 0) !== 0 || (r.second_choice || 0) !== 0;

export default function Inventory() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [sp] = useSearchParams();
  const qLabelOf = (q) => t(`quality.${q}`, q);
  const canEdit = can(user, "inventory:edit");
  const canCost = can(user, "finance:view");
  // MHD-Zuordnung/-Korrektur ist Teil der Rückverfolgbarkeit: nur admin + lager.
  const canEditBbd = can(user, "inventory:editBbd");
  const canRename = can(user, "inventory:rename");
  const [rows, setRows] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [tab, setTab] = useState(sp.get("tab") === "mhd" ? "mhd" : "aktuell");
  const [lotGroups, setLotGroups] = useState({});
  const [locEnabled, setLocEnabled] = useState(false);
  const [locations, setLocations] = useState([]);
  const [transferFor, setTransferFor] = useState(null); // {variant, lots}
  const [writeOffRow, setWriteOffRow] = useState(null);
  const [bbdRow, setBbdRow] = useState(null);
  // Ansichtseinstellung des Benutzers (serverseitig am Konto gespeichert, Default: aus).
  const [showZero, setShowZero] = useState(!!user?.ui_prefs?.inventory_show_zero_stock);
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState("");
  const [adj, setAdj] = useState(null);
  const [af, setAf] = useState(EMPTY_ADJ);
  const [hist, setHist] = useState(null); // {sku, moves}
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", physical: "", second: "", reason: "" });
  const [editBusy, setEditBusy] = useState(false);
  const [own, setOwn] = useState(null);
  const [ownQty, setOwnQty] = useState("");
  const [ownNote, setOwnNote] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoQty, setPromoQty] = useState("");
  const [promoEvent, setPromoEvent] = useState("");
  const [promoNote, setPromoNote] = useState("");

  useEffect(() => {
    setShowZero(!!user?.ui_prefs?.inventory_show_zero_stock);
  }, [user?.id, user?.ui_prefs?.inventory_show_zero_stock]);

  const toggleShowZero = (on) => {
    setShowZero(on);
    api.patch("/auth/me/preferences", { inventory_show_zero_stock: on }).catch(() => {});
  };

  const load = useCallback(() => {
    api.get("/inventory").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    api.get("/inventory/lot-overview").then((r) => {
      setLotGroups(Object.fromEntries((r.data.groups || []).map((g) => [g.variant_id, g])));
      setLocEnabled(!!r.data.location_management_enabled);
    }).catch(() => {});
    api.get("/storage-locations").then((r) => setLocations(r.data.locations || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEditRow = (row) => {
    setEditRow(row);
    setEditForm({ name: row.product_name || "", physical: String(row.physical ?? 0),
      second: String(row.second_choice ?? 0), reason: "" });
  };

  /** Speichert Name und/oder Mengen. Mengen laufen als Korrekturbuchung über das Ledger,
   *  damit jede Änderung nachvollziehbar bleibt (append-only). */
  const saveEditRow = async () => {
    const row = editRow;
    const name = editForm.name.trim();
    const renaming = canRename && name !== row.product_name;
    if (canRename && renaming && !name) { toast.error(t("inv.nameRequired")); return; }
    const diffs = [];
    if (canEdit) {
      const good = Number(editForm.physical);
      const second = Number(editForm.second);
      if (!isNaN(good) && good !== row.physical) diffs.push(["good", good - row.physical]);
      if (!isNaN(second) && second !== row.second_choice) {
        diffs.push(["second_choice", second - row.second_choice]);
      }
    }
    if (!renaming && !diffs.length) { toast.info(t("inv.noChanges")); return; }
    setEditBusy(true);
    try {
      if (renaming) {
        await api.put(`/variants/${row.variant_id}`, { product_name: name });
        toast.success(t("inv.renamed"));
      }
      for (const [quality, diff] of diffs) {
        await api.post("/stock-movements/adjust", {
          variant_id: row.variant_id, warehouse_id: warehouses[0]?.id, quantity_signed: diff,
          quality,
          reason: editForm.reason.trim() || `${t("inv.manualAdjust")} · ${qLabelOf(quality)}`,
        });
      }
      if (diffs.length) toast.success(t("inv.movementPosted"));
      setEditRow(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setEditBusy(false); }
  };

  const bookOwn = async () => {
    const qty = Number(ownQty);
    if (!qty || qty <= 0) { toast.error(t("errors.quantityPositive")); return; }
    try {
      await api.post("/own-withdrawals", { variant_id: own.variant_id, warehouse_id: warehouses[0]?.id, quantity: qty, note: ownNote });
      toast.success(t("inv.ownWithdrawalDone"));
      setOwn(null); setOwnQty(""); setOwnNote(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const bookPromo = async () => {
    const qty = Number(promoQty);
    if (!qty || qty <= 0) { toast.error(t("errors.quantityPositive")); return; }
    try {
      await api.post("/promotions", { variant_id: promo.variant_id, warehouse_id: warehouses[0]?.id, quantity: qty, event: promoEvent, note: promoNote });
      toast.success(t("inv.promotionDone"));
      setPromo(null); setPromoQty(""); setPromoEvent(""); setPromoNote(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveAdj = async () => {
    const wh = warehouses[0]?.id;
    const qty = Number(af.quantity_signed);
    const qLabel = qLabelOf(af.quality);
    try {
      if (af.reclass) {
        const n = Math.abs(qty);
        if (!n) { toast.error(t("errors.invalidQuantity")); return; }
        const reason = af.reason || `${t("inv.correction")}: ${t("quality.good")} → ${qLabel}`;
        await api.post("/stock-movements/adjust", { variant_id: adj.variant_id, warehouse_id: wh, quantity_signed: -n, quality: "good", reason });
        await api.post("/stock-movements/adjust", { variant_id: adj.variant_id, warehouse_id: wh, quantity_signed: n, quality: af.quality, reason });
        toast.success(t("inv.reclassDone", { n, quality: qLabel }));
      } else {
        await api.post("/stock-movements/adjust", { variant_id: adj.variant_id, warehouse_id: wh, quantity_signed: qty, quality: af.quality, reason: af.reason });
        toast.success(t("inv.movementPosted"));
      }
      setAdj(null); setAf(EMPTY_ADJ); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const showHist = async (row) => {
    const r = await api.get(`/stock-movements?variant_id=${row.variant_id}`);
    setHist({ sku: row.sku, moves: r.data });
  };


  const needle = q.trim().toLowerCase();
  const filtered = (rows || [])
    .filter((r) => tab !== "unter" || r.understock)
    .filter((r) => showZero || tab === "unter" || hasStock(r))
    .filter((r) => !needle || (r.product_name || "").toLowerCase().includes(needle)
      || (r.sku || "").toLowerCase().includes(needle));
  const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, INV_ACC);

  return (
    <div className="flex h-full flex-col overflow-x-hidden">
      <PageHeader title={t("inv.title")} subtitle={t("inv.subtitle")} />
      <Tabs value={tab} onValueChange={setTab} className="mb-4 shrink-0">
        <TabsList className="max-w-full overflow-x-auto">
          <TabsTrigger value="aktuell" data-testid="tab-aktuell">{t("inv.overviewTab")}</TabsTrigger>
          <TabsTrigger value="unter" data-testid="tab-unter">{t("inv.understockTab")}</TabsTrigger>
          <TabsTrigger value="mhd" data-testid="tab-mhd">{t("ex.tab")}</TabsTrigger>
          <TabsTrigger value="umlagerungen" data-testid="tab-umlagerungen">{t("tr.tab")}</TabsTrigger>
          <TabsTrigger value="eigenentnahmen" data-testid="tab-eigenentnahmen">{t("ow.title")}</TabsTrigger>
          <TabsTrigger value="promotion" data-testid="tab-promotion">{t("pr.title")}</TabsTrigger>
          <TabsTrigger value="abschreibungen" data-testid="tab-abschreibungen">{t("wo.tab")}</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "mhd" ? <ExpiryTab initialStatus={sp.get("status") || ""} /> :
        tab === "umlagerungen" ? <TransfersTab /> :
          tab === "eigenentnahmen" ? <OwnWithdrawals embedded /> :
            tab === "promotion" ? <PromotionsPage embedded /> :
              tab === "abschreibungen" ? <WriteOffsTab /> : (<>
            <div className="mb-4 flex flex-wrap items-center gap-4 shrink-0">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("inv.searchPlaceholder")}
                data-testid="inventory-search"
                className="w-full max-w-sm h-10 rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Switch checked={showZero} onCheckedChange={toggleShowZero} data-testid="inventory-show-zero" />
                {t("inv.showZeroStock")}
              </label>
            </div>

            {rows === null ? <div className="text-muted-foreground">{t("common.loading")}</div> :
              filtered.length === 0
                ? <EmptyState testId="inventory-empty" icon={Warehouse} title={t("inv.emptyTitle")} hint={t("inv.emptyHint")} />
                : <InventoryTable rows={sorted} lotGroups={lotGroups} expanded={expanded} setExpanded={setExpanded}
                    canEdit={canEdit} canRename={canRename} canEditBbd={canEditBbd} canCost={canCost}
                    locEnabled={locEnabled} qLabelOf={qLabelOf}
                    onHistory={showHist}
                    onOwn={(r) => { setOwn(r); setOwnQty(""); setOwnNote(""); }}
                    onPromo={(r) => { setPromo(r); setPromoQty(""); setPromoEvent(""); setPromoNote(""); }}
                    onAdjust={setAdj} onTransfer={setTransferFor} onWriteOff={setWriteOffRow}
                    onBbd={setBbdRow} onEditRow={openEditRow}
                    sortKey={sortKey} sortDir={sortDir} onSort={toggle} />}
          </>)}

      <TransferDialog open={!!transferFor} onClose={() => setTransferFor(null)} onDone={load}
        variant={transferFor?.variant} lots={transferFor?.lots} locations={locations} />
      <WriteOffDialog open={!!writeOffRow} onClose={() => setWriteOffRow(null)} onDone={load} row={writeOffRow} />
      <LotBbdDialog open={!!bbdRow} onClose={() => setBbdRow(null)} onDone={load} row={bbdRow} />

      <AdjustDialog adj={adj} onClose={() => setAdj(null)} af={af} setAf={setAf} onSave={saveAdj} />
      <HistoryDialog hist={hist} onClose={() => setHist(null)} qLabelOf={qLabelOf} />
      <RenameDialog row={editRow} onClose={() => setEditRow(null)} form={editForm}
        setForm={setEditForm} canRename={canRename} canEdit={canEdit} busy={editBusy}
        onSave={saveEditRow} />
      <OwnWithdrawalDialog own={own} onClose={() => setOwn(null)} qty={ownQty} setQty={setOwnQty}
        note={ownNote} setNote={setOwnNote} onSave={bookOwn} email={user?.email} />
      <PromotionDialog promo={promo} onClose={() => setPromo(null)} qty={promoQty} setQty={setPromoQty}
        event={promoEvent} setEvent={setPromoEvent} note={promoNote} setNote={setPromoNote} onSave={bookPromo} />
    </div>
  );
}
